import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  ArrowRight, FileText, Search, Globe, Linkedin,
  ChevronRight, Star, TrendingUp, Users, Zap
} from "lucide-react";
import NeuralNetwork from "../components/NeuralNetwork";

gsap.registerPlugin(ScrollTrigger);

// ── Animation Variants ──────────────────────────────────────────────
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12, duration: 0.6, ease: "easeOut" } }) };

// ── Service Cards ────────────────────────────────────────────────────
const services = [
  { icon: FileText, title: "Content Creation", desc: "AI-crafted content that converts: blogs, copy, and campaigns at 10x speed.", color: "#4F8EF7" },
  { icon: Globe, title: "GEO / AEO Consultancy", desc: "Dominate AI search engines and answer engines with optimised structured content.", color: "#8B5CF6" },
  { icon: Search, title: "SEO Consultancy", desc: "Data-driven SEO strategies that rank your brand where it matters most.", color: "#4F8EF7" },
  { icon: Linkedin, title: "LinkedIn Marketing", desc: "Precision LinkedIn campaigns that build authority and drive qualified B2B leads.", color: "#8B5CF6" },
];

// ── Counter Data ─────────────────────────────────────────────────────
const stats = [
  { value: 10, suffix: "x", label: "Faster Content Production" },
  { value: 3, suffix: "x", label: "Average ROI Improvement" },
  { value: 95, suffix: "%", label: "Client Retention Rate" },
  { value: 50, suffix: "+", label: "Brands Transformed" },
];

// ── Testimonials ─────────────────────────────────────────────────────
const testimonials = [
  { name: "Rick Sagoo", role: "CEO at SyncdCX", text: "EnGenius Marketing redefined our content strategy. Our organic traffic tripled in 4 months.", rating: 5 },
  { name: "Vinesh Nair", role: "COO at VisionSync", text: "The AI-powered SEO work was phenomenal. We went from page 5 to page 1 in 6 weeks.", rating: 5 },
  { name: "Vaidya Shankar", role: "Director at iNetFrame Technologies", text: "Their LinkedIn campaigns generated more qualified leads than any channel we had tried.", rating: 5 },
];

// ── Process Steps ─────────────────────────────────────────────────────
const steps = [
  { num: "01", title: "Audit", desc: "Deep-dive analysis of your current marketing, SEO footprint, and competitive landscape using AI-powered tools." },
  { num: "02", title: "Strategy", desc: "We craft a tailored AI-backed growth strategy with every channel, every asset, and every KPI mapped to your goals." },
  { num: "03", title: "Execute", desc: "Rapid deployment of campaigns, content, and optimisations. We move fast and iterate with data, not guesswork." },
];

// ══════════════════════════════════════════════════════════════════════
const Home = () => {
  const counterRef = useRef(null);
  const stepsRef = useRef(null);
  const cardsRef = useRef(null);
  const countersAnimated = useRef(false);

  // GSAP: Counter animation
  useEffect(() => {
    const el = counterRef.current;
    if (!el) return;
    const trigger = ScrollTrigger.create({
      trigger: el,
      start: "top 80%",
      onEnter: () => {
        if (countersAnimated.current) return;
        countersAnimated.current = true;
        const items = el.querySelectorAll("[data-counter]");
        items.forEach((item) => {
          const target = parseFloat(item.dataset.counter);
          gsap.fromTo(item, { textContent: 0 }, {
            textContent: target,
            duration: 2,
            ease: "power2.out",
            snap: { textContent: target % 1 === 0 ? 1 : 0.1 },
            onUpdate() { item.textContent = Math.round(parseFloat(item.textContent)); },
          });
        });
      },
    });
    return () => trigger.kill();
  }, []);

  // GSAP: Steps timeline animation
  useEffect(() => {
    const el = stepsRef.current;
    if (!el) return;
    const items = el.querySelectorAll(".step-item");
    const tl = gsap.timeline({
      scrollTrigger: { trigger: el, start: "top 70%", end: "bottom 20%", scrub: false },
    });
    items.forEach((item, i) => {
      tl.fromTo(item, { opacity: 0, x: i % 2 === 0 ? -40 : 40 }, { opacity: 1, x: 0, duration: 0.6, ease: "power2.out" }, i * 0.2);
    });
    return () => ScrollTrigger.getAll().forEach((t) => t.kill());
  }, []);

  // GSAP: Cards reveal
  useEffect(() => {
    const el = cardsRef.current;
    if (!el) return;
    const cards = el.querySelectorAll(".service-card");
    gsap.fromTo(cards,
      { opacity: 0, y: 50 },
      {
        opacity: 1, y: 0, duration: 0.65, stagger: 0.12, ease: "power2.out",
        scrollTrigger: { trigger: el, start: "top 75%" },
      }
    );
  }, []);

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="home-page"
      style={{ backgroundColor: "#0a0a0f" }}
    >
      {/* ── HERO ────────────────────────────────────────────────────── */}
      <section
        data-testid="hero-section"
        style={{ minHeight: "100vh", position: "relative", overflow: "hidden", display: "flex", alignItems: "center", backgroundColor: "#0a0a0f" }}
      >
        <NeuralNetwork />

        {/* Gradient overlays */}
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 20% 50%, rgba(79,142,247,0.12) 0%, transparent 60%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 80% 30%, rgba(139,92,246,0.1) 0%, transparent 55%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "180px", background: "linear-gradient(to top, #0a0a0f, transparent)", pointerEvents: "none" }} />

        <div className="container-custom relative z-10 pt-24 pb-16 text-center">
          {/* Badge */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={0}
            className="inline-flex items-center gap-2 mb-8 px-4 py-2 rounded-full text-xs font-medium"
            style={{ background: "rgba(79,142,247,0.1)", border: "1px solid rgba(79,142,247,0.25)", color: "#4F8EF7" }}
            data-testid="hero-badge"
          >
            <Zap size={12} />
            AI-Powered Marketing Agency
          </motion.div>

          {/* Headline */}
          <div className="mb-6" style={{ paddingBottom: "4px" }}>
            {["Marketing,", "Engineered by AI."].map((line, i) => (
              <motion.h1
                key={i}
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                custom={i + 1}
                className="block font-heading font-bold"
                style={{
                  fontSize: "clamp(2.5rem, 7vw, 5.5rem)",
                  lineHeight: "1.15",
                  color: i === 1 ? "transparent" : "#ffffff",
                  backgroundImage: i === 1 ? "linear-gradient(135deg, #4F8EF7, #8B5CF6)" : undefined,
                  WebkitBackgroundClip: i === 1 ? "text" : undefined,
                  WebkitTextFillColor: i === 1 ? "transparent" : undefined,
                  backgroundClip: i === 1 ? "text" : undefined,
                  paddingBottom: i === 1 ? "6px" : "0",
                }}
                data-testid={`hero-headline-${i}`}
              >
                {line}
              </motion.h1>
            ))}
          </div>

          {/* Subheading */}
          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={3}
            className="text-base md:text-lg max-w-2xl mx-auto mb-10 leading-relaxed"
            style={{ color: "#a1a1aa" }}
            data-testid="hero-subheading"
          >
            We combine cutting-edge AI with proven marketing frameworks to accelerate your growth: faster content, smarter SEO, and campaigns that actually convert.
          </motion.p>

          {/* CTAs */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={4}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
            data-testid="hero-ctas"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-primary"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-white text-sm transition-all duration-300"
              style={{
                background: "linear-gradient(135deg, #4F8EF7, #8B5CF6)",
                boxShadow: "0 0 25px rgba(79,142,247,0.4)",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px) scale(1.02)"; e.currentTarget.style.boxShadow = "0 0 40px rgba(79,142,247,0.6)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 0 25px rgba(79,142,247,0.4)"; }}
            >
              Get Started <ArrowRight size={16} />
            </Link>
            <Link
              to="/services"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-medium text-white text-sm transition-all duration-300"
              style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
            >
              See Our Work <ChevronRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── SERVICES ─────────────────────────────────────────────────── */}
      <section data-testid="services-section" className="section-padding" style={{ backgroundColor: "#0a0a0f" }}>
        <div className="container-custom">
          <div className="text-center mb-10">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-xs font-mono uppercase tracking-widest mb-3"
              style={{ color: "#4F8EF7" }}
            >
              What We Do
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: 0.1 }}
              className="font-heading font-bold text-4xl md:text-5xl text-white mb-4"
            >
              Services Built for <span className="gradient-text">Scale</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-base max-w-xl mx-auto"
              style={{ color: "#a1a1aa" }}
            >
              Four core disciplines, powered by AI, designed to move fast and deliver real business outcomes.
            </motion.p>
          </div>

          <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {services.map((svc, i) => {
              const Icon = svc.icon;
              return (
                <motion.div
                  key={svc.title}
                  className="service-card"
                  whileHover={{ y: -8, scale: 1.01 }}
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                  data-testid={`service-card-${i}`}
                  style={{
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.07)",
                    borderRadius: "16px",
                    padding: "1.75rem",
                    cursor: "default",
                    position: "relative",
                    overflow: "hidden",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.border = `1px solid ${svc.color}40`;
                    e.currentTarget.style.boxShadow = `0 20px 50px ${svc.color}15`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.border = "1px solid rgba(255,255,255,0.07)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  {/* Gradient top line */}
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "2px", background: `linear-gradient(90deg, ${svc.color}, transparent)`, opacity: 0.6 }} />
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-5"
                    style={{ background: `${svc.color}15`, border: `1px solid ${svc.color}25` }}
                  >
                    <Icon size={20} style={{ color: svc.color }} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading font-semibold text-white text-base mb-2">{svc.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "#71717a" }}>{svc.desc}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/services"
              data-testid="services-view-all"
              className="inline-flex items-center gap-2 text-sm font-medium transition-colors duration-200"
              style={{ color: "#4F8EF7" }}
            >
              View all services <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── WHY AI ───────────────────────────────────────────────────── */}
      <section data-testid="why-ai-section" className="section-padding" style={{ backgroundColor: "#080810" }}>
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            {/* Left: image */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className="relative"
            >
              <div style={{ borderRadius: "20px", overflow: "hidden", border: "1px solid rgba(79,142,247,0.2)", boxShadow: "0 30px 80px rgba(79,142,247,0.12)" }}>
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/f8936331-82f6-497f-871a-606e003b0793/images/8016955e3212c8e77c4460778065992d9e3ac1a92ff4fa04bc2b7460ba6c2b53.png"
                  alt="AI neural network visualization"
                  style={{ width: "100%", display: "block" }}
                />
              </div>
              {/* Floating badge */}
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -bottom-5 -right-5 px-5 py-3 rounded-2xl glass-strong"
                style={{ border: "1px solid rgba(79,142,247,0.3)" }}
              >
                <p className="text-xs font-mono" style={{ color: "#71717a" }}>AI Confidence</p>
                <p className="font-heading font-bold text-lg" style={{ color: "#4F8EF7" }}>98.7%</p>
              </motion.div>
            </motion.div>

            {/* Right: counters */}
            <div>
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="text-xs font-mono uppercase tracking-widest mb-3"
                style={{ color: "#8B5CF6" }}
              >
                Why AI?
              </motion.p>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: 0.1 }}
                className="font-heading font-bold text-4xl md:text-5xl text-white mb-5"
              >
                Results That <span className="gradient-text">Speak Data</span>
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-base leading-relaxed mb-10"
                style={{ color: "#a1a1aa" }}
              >
                AI removes the guesswork. Every piece of content, every keyword, every campaign is backed by real-time data and intelligent models that learn as they run.
              </motion.p>

              <div ref={counterRef} className="grid grid-cols-2 gap-6">
                {stats.map((s, i) => (
                  <motion.div
                    key={s.label}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    data-testid={`stat-item-${i}`}
                    className="p-5 rounded-2xl"
                    style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}
                  >
                    <div className="flex items-baseline gap-0.5 mb-1">
                      <span
                        data-counter={s.value}
                        className="font-heading font-bold text-3xl gradient-text"
                        data-testid={`counter-value-${i}`}
                      >
                        {s.value}
                      </span>
                      <span className="font-heading font-bold text-2xl gradient-text">{s.suffix}</span>
                    </div>
                    <p className="text-xs" style={{ color: "#71717a" }}>{s.label}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────────────────── */}
      <section data-testid="how-it-works-section" className="section-padding" style={{ backgroundColor: "#0a0a0f" }}>
        <div className="container-custom">
          <div className="text-center mb-10">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-xs font-mono uppercase tracking-widest mb-3"
              style={{ color: "#4F8EF7" }}
            >
              The Process
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: 0.1 }}
              className="font-heading font-bold text-4xl md:text-5xl text-white"
            >
              How It <span className="gradient-text">Works</span>
            </motion.h2>
          </div>

          <div ref={stepsRef} className="relative max-w-3xl mx-auto">
            {/* Connecting line */}
            <div
              className="hidden md:block absolute left-[2.2rem] top-10 bottom-10"
              style={{ width: "2px", background: "linear-gradient(to bottom, #4F8EF7, #8B5CF6)", opacity: 0.3 }}
            />

            <div className="flex flex-col gap-8">
              {steps.map((step, i) => (
                <motion.div
                  key={step.num}
                  className="step-item flex gap-6 items-start"
                  data-testid={`step-item-${i}`}
                >
                  <div
                    className="flex-shrink-0 w-[4.4rem] h-[4.4rem] rounded-2xl flex flex-col items-center justify-center relative z-10"
                    style={{
                      background: "#0a0a0f",
                      border: "2px solid",
                      borderColor: i % 2 === 0 ? "#4F8EF7" : "#8B5CF6",
                      boxShadow: `0 0 20px ${i % 2 === 0 ? "rgba(79,142,247,0.2)" : "rgba(139,92,246,0.2)"}`,
                    }}
                  >
                    <span className="font-mono text-xs font-bold" style={{ color: i % 2 === 0 ? "#4F8EF7" : "#8B5CF6" }}>{step.num}</span>
                  </div>
                  <div className="pt-2 flex-1">
                    <h3 className="font-heading font-bold text-white text-xl mb-2">{step.title}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: "#71717a" }}>{step.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ─────────────────────────────────────────────── */}
      <section data-testid="testimonials-section" className="section-padding" style={{ backgroundColor: "#080810" }}>
        <div className="container-custom">
          <div className="text-center mb-10">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-xs font-mono uppercase tracking-widest mb-3"
              style={{ color: "#8B5CF6" }}
            >
              Client Stories
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: 0.1 }}
              className="font-heading font-bold text-4xl md:text-5xl text-white"
            >
              Trusted by <span className="gradient-text">Growth Teams</span>
            </motion.h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.13 }}
                whileHover={{ y: -6 }}
                data-testid={`testimonial-card-${i}`}
                style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.07)",
                  borderRadius: "16px",
                  padding: "1.75rem",
                  position: "relative",
                }}
              >
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} size={14} style={{ color: "#4F8EF7", fill: "#4F8EF7" }} />
                  ))}
                </div>
                <p className="text-sm leading-relaxed mb-5" style={{ color: "#a1a1aa" }}>"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center font-heading font-bold text-sm text-white"
                    style={{ background: "linear-gradient(135deg, #4F8EF7, #8B5CF6)" }}
                  >
                    {t.name[0]}
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-white">{t.name}</p>
                    <p className="text-xs" style={{ color: "#52525b" }}>{t.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ───────────────────────────────────────────────── */}
      <section data-testid="cta-banner-section" className="section-padding" style={{ backgroundColor: "#0a0a0f" }}>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden text-center rounded-3xl px-8 py-16 md:px-16"
            style={{
              background: "linear-gradient(135deg, rgba(79,142,247,0.12) 0%, rgba(139,92,246,0.1) 100%)",
              border: "1px solid rgba(79,142,247,0.2)",
            }}
          >
            {/* Radial glow */}
            <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 50% 50%, rgba(79,142,247,0.08), transparent 65%)", pointerEvents: "none" }} />

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="relative z-10"
            >
              <p className="text-xs font-mono uppercase tracking-widest mb-4" style={{ color: "#4F8EF7" }}>
                Ready to scale?
              </p>
              <h2 className="font-heading font-bold text-3xl md:text-5xl text-white mb-5">
                Ready to Grow <span className="gradient-text">Smarter?</span>
              </h2>
              <p className="text-base mb-8 max-w-xl mx-auto" style={{ color: "#a1a1aa" }}>
                Join companies that have accelerated their marketing with AI. Let's engineer your growth story together.
              </p>
              <motion.div
                animate={{ boxShadow: ["0 0 20px rgba(79,142,247,0.3)", "0 0 40px rgba(79,142,247,0.55)", "0 0 20px rgba(79,142,247,0.3)"] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                className="inline-block rounded-full"
              >
                <Link
                  to="/contact"
                  data-testid="cta-banner-button"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-white text-base transition-all duration-300"
                  style={{ background: "linear-gradient(135deg, #4F8EF7, #8B5CF6)" }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.05)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                >
                  Start Your Journey <ArrowRight size={18} />
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </motion.main>
  );
};

export default Home;
